import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Copy, UsersThree, X, Trophy } from '@phosphor-icons/react';
import Avatar from '../components/Avatar';
import ReactionsRow from '../components/ReactionsRow';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Crew() {
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null); // shows workouts modal
  const [showCreate, setShowCreate] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadGroups(); }, []);

  const loadGroups = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/groups`, { withCredentials: true });
      // Get member counts + members for each
      const enriched = await Promise.all(
        data.map(async (g) => {
          try {
            const { data: full } = await axios.get(`${API_URL}/groups/${g.id}`, { withCredentials: true });
            return { ...g, ...full };
          } catch { return g; }
        })
      );
      setGroups(enriched);
    } finally { setLoading(false); }
  };

  if (loading) {
    return (
      <div className="mobile-container min-h-screen flex items-center justify-center">
        <div className="animate-pulse-soft text-6xl font-black font-heading text-graphite">B</div>
      </div>
    );
  }

  return (
    <div className="mobile-container min-h-screen">
      <div className="content-wrapper px-5 pt-10">
        <div className="flex items-baseline justify-between mb-6 animate-slide-up">
          <div>
            <p className="text-xs uppercase tracking-[0.24em] text-graphite/50 font-semibold mb-1">
              Optional
            </p>
            <h1 className="text-5xl font-black font-heading uppercase tracking-tight text-graphite leading-none">
              Squad
            </h1>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowJoin(true)}
              className="h-11 px-4 border border-graphite/30 text-graphite font-bold text-xs uppercase tracking-[0.15em] rounded-ios-pill"
              data-testid="join-group-button"
            >
              Join
            </button>
            <button
              onClick={() => setShowCreate(true)}
              className="h-11 px-4 bg-graphite text-bone font-bold text-xs uppercase tracking-[0.15em] rounded-ios-pill flex items-center gap-1.5"
              data-testid="create-group-button"
            >
              <Plus size={14} weight="bold" />
              New
            </button>
          </div>
        </div>

        <p className="text-sm text-graphite/60 mb-6">
          Squads are optional. Team up to share routines and compare stats with your training partners.
        </p>

        {groups.length === 0 ? (
          <div className="ios-card p-8 text-center">
            <UsersThree size={48} weight="fill" className="text-graphite/30 mx-auto mb-4" />
            <h2 className="text-xl font-black font-heading uppercase text-graphite mb-2">
              No Squads Yet
            </h2>
            <p className="text-sm text-graphite/60">
              Create one to invite your training partner, or join an existing squad with a code.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {groups.map((g) => (
              <button
                key={g.id}
                onClick={() => setSelectedGroup(g)}
                className="ios-card p-4 text-left animate-pop-in"
                data-testid={`group-card-${g.id}`}
              >
                <div className="mb-2 flex items-center justify-between">
                  <div className="w-10 h-10 rounded-ios-pill bg-partnerBlue/15 flex items-center justify-center">
                    <UsersThree size={18} weight="fill" className="text-partnerBlue" />
                  </div>
                  <p className="text-3xl font-black font-heading tabular-nums text-graphite">
                    {g.members?.length || g.member_count || 1}
                  </p>
                </div>
                <h3 className="text-base font-black font-heading uppercase text-graphite tracking-tight truncate">
                  {g.name}
                </h3>
                <p className="text-[10px] uppercase tracking-[0.15em] text-graphite/50 font-semibold mt-0.5">
                  Members
                </p>
              </button>
            ))}
          </div>
        )}
      </div>

      {selectedGroup && (
        <GroupWorkoutsModal
          group={selectedGroup}
          user={user}
          onClose={() => setSelectedGroup(null)}
        />
      )}
      {showCreate && <CreateGroupModal onClose={() => setShowCreate(false)} onCreated={loadGroups} />}
      {showJoin && <JoinGroupModal onClose={() => setShowJoin(false)} onJoined={loadGroups} />}
</div>
  );
}

function GroupWorkoutsModal({ group, user, onClose }) {
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(
          `${API_URL}/progress/activity?group_id=${group.id}`,
          { withCredentials: true }
        );
        setActivity(data);
      } finally { setLoading(false); }
    })();
  }, [group.id]);

  const copyInvite = () => {
    navigator.clipboard.writeText(group.invite_code);
  };

  return (
    <div className="fixed inset-0 z-[1100] bg-graphite/40 backdrop-blur-sm flex items-end sm:items-center justify-center px-3 pb-3">
      <div className="w-full max-w-md bg-bone rounded-ios-xl shadow-ios overflow-hidden max-h-[85vh] flex flex-col animate-slide-up">
        <div className="px-5 py-4 border-b border-hairline">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/50">
                Squad
              </p>
              <h2 className="text-2xl font-black font-heading uppercase text-graphite truncate">
                {group.name}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-ios-pill border border-hairline flex items-center justify-center"
              data-testid="close-group-modal-button"
            >
              <X size={18} weight="bold" />
            </button>
          </div>

          <button
            onClick={copyInvite}
            className="w-full h-11 px-3 rounded-ios-pill bg-graphite/5 text-graphite font-bold text-xs uppercase tracking-[0.15em] flex items-center justify-between"
            data-testid="copy-invite-button"
          >
            <span>Invite code · {group.invite_code}</span>
            <Copy size={14} weight="bold" />
          </button>
        </div>

        <div className="px-5 py-4 border-b border-hairline">
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/50 mb-2">
            Members
          </p>
          <div className="flex gap-2 flex-wrap">
            {group.members?.map((m) => (
              <div
                key={m.id}
                className={`h-9 px-3 rounded-ios-pill flex items-center gap-2 ${
                  m.id === user.id ? 'bg-userRed/15' : 'bg-graphite/5'
                }`}
                data-testid={`member-${m.id}`}
              >
                <Avatar
                  user={m}
                  size={24}
                  tone={m.id === user.id ? 'red' : 'graphite'}
                  testId={`member-avatar-${m.id}`}
                />
                <span className={`text-xs font-bold ${m.id === user.id ? 'text-userRed' : 'text-graphite'}`}>
                  {m.display_name}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <h3 className="text-lg font-black font-heading uppercase text-graphite mb-3">
            Recent Sessions
          </h3>
          {loading ? (
            <p className="text-sm text-graphite/50 text-center py-4">Loading...</p>
          ) : activity.length === 0 ? (
            <p className="text-sm text-graphite/50 text-center py-4">
              No workouts logged in this squad yet.
            </p>
          ) : (
            <div className="space-y-2">
              {activity.map((w, i) => (
                <ActivityRow key={w.id || i} w={w} idx={i} isMe={w.profile?.id === user.id} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MiniInline({ label, value, unit }) {
  return (
    <div className="flex items-baseline gap-1">
      <span className="text-lg font-black font-heading tabular-nums text-graphite">
        {value}
        {unit && <span className="text-[10px] font-normal ml-0.5">{unit}</span>}
      </span>
      <span className="text-[9px] uppercase tracking-[0.18em] font-bold text-graphite/50">{label}</span>
    </div>
  );
}

function CreateGroupModal({ onClose, onCreated }) {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/groups`, { name }, { withCredentials: true });
      await onCreated();
      onClose();
    } catch { alert('Failed to create'); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-[1100] bg-graphite/40 backdrop-blur-sm flex items-end sm:items-center justify-center px-3 pb-3">
      <div className="w-full max-w-md bg-bone rounded-ios-xl shadow-ios overflow-hidden animate-slide-up">
        <div className="px-5 py-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-black font-heading uppercase text-graphite">New Squad</h2>
            <button onClick={onClose} className="w-10 h-10 rounded-ios-pill border border-hairline flex items-center justify-center"><X size={18} weight="bold" /></button>
          </div>
          <form onSubmit={submit} className="space-y-3">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Squad name"
              required
              className="w-full h-14 px-4 rounded-ios-pill border border-hairline bg-boneWarm focus:outline-none focus:ring-2 focus:ring-graphite text-base"
              data-testid="group-name-input"
              autoFocus
            />
            <button
              type="submit"
              disabled={loading || !name.trim()}
              className="w-full h-14 bg-graphite text-bone font-bold text-sm uppercase tracking-[0.15em] rounded-ios-pill disabled:opacity-50"
              data-testid="create-button"
            >
              {loading ? 'Creating...' : 'Create Squad'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function JoinGroupModal({ onClose, onJoined }) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/groups/join`, { invite_code: code }, { withCredentials: true });
      await onJoined();
      onClose();
    } catch { alert('Invalid invite code'); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-[1100] bg-graphite/40 backdrop-blur-sm flex items-end sm:items-center justify-center px-3 pb-3">
      <div className="w-full max-w-md bg-bone rounded-ios-xl shadow-ios overflow-hidden animate-slide-up">
        <div className="px-5 py-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-black font-heading uppercase text-graphite">Join Squad</h2>
            <button onClick={onClose} className="w-10 h-10 rounded-ios-pill border border-hairline flex items-center justify-center"><X size={18} weight="bold" /></button>
          </div>
          <form onSubmit={submit} className="space-y-3">
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Invite code"
              required
              className="w-full h-14 px-4 rounded-ios-pill border border-hairline bg-boneWarm focus:outline-none focus:ring-2 focus:ring-graphite text-base"
              data-testid="invite-code-input"
              autoFocus
            />
            <button
              type="submit"
              disabled={loading || !code.trim()}
              className="w-full h-14 bg-graphite text-bone font-bold text-sm uppercase tracking-[0.15em] rounded-ios-pill disabled:opacity-50"
              data-testid="join-button"
            >
              {loading ? 'Joining...' : 'Join Squad'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function ActivityRow({ w, idx, isMe }) {
  const avatarRef = React.useRef(null);
  return (
    <div
      className="border border-hairline rounded-ios p-3 bg-boneWarm"
      data-testid={`activity-${idx}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="min-w-0 flex items-center gap-2">
          <span ref={avatarRef} className="relative inline-flex">
            <Avatar
              user={w.profile}
              size={28}
              tone={isMe ? 'red' : 'blue'}
              testId={`activity-avatar-${idx}`}
            />
          </span>
          <div>
            <div className="flex items-center gap-1.5">
              <span className={`text-sm font-black ${isMe ? 'text-userRed' : 'text-partnerBlue'}`}>
                {w.profile?.display_name}
              </span>
              <span className="text-xs text-graphite/50">·</span>
              <span className="text-xs text-graphite/70">{w.routine_name}</span>
            </div>
          </div>
        </div>
        <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-graphite/50">
          {w.finished_at && format(new Date(w.finished_at), 'MMM d')}
        </p>
      </div>
      <div className="flex items-center gap-4">
        <MiniInline label="Sets" value={w.total_sets} />
        <MiniInline label="Vol" value={Math.round(w.total_volume)} unit="kg" />
        {w.personal_bests > 0 && (
          <div className="ml-auto flex items-center gap-1 px-2 py-0.5 rounded-ios-pill bg-userRed text-bone">
            <Trophy size={10} weight="fill" />
            <span className="text-[10px] font-black uppercase tracking-[0.15em]">
              {w.personal_bests} PB
            </span>
          </div>
        )}
      </div>
      {w.id && !isMe && (
        <div className="mt-2 pt-2 border-t border-hairline/60">
          <ReactionsRow
            workoutId={w.id}
            anchorRef={avatarRef}
            testId={`activity-reactions-${idx}`}
          />
        </div>
      )}
    </div>
  );
}
